import React, { useEffect, useState } from 'react';
import classPrefix from 'prefix-classnames';
import { Modal, Steps } from 'antd';
import { useSelector, useIntl, useDispatch, type UserState } from 'umi';
import { usePersistFn } from 'ahooks';
import { stepInfo } from '@/utils/FosterServiceConf';
import FooterButton from '@/components/FooterButton';
import type { FosterStepModelsState } from 'umi';
import Breadcrumb from '@/components/Breadcrumb';
import { getWebFosterBreadcrumbItems } from '@/utils/Breadcrumb';
import type { StepInfoType } from './types/defs';
import StepRender from './components/StepRender';
import useHandleStep from './components/StepRender/HandleStep';

import './css-web/WebFosterCareService.less';
import GuideButton from '@/components/GuideButton';
import { calculateAge, formatDateToYYYYMMDD } from '@/utils/dateutils';
import { commonPostRequest, removeToken } from '@/utils/apiUtils';

const px = classPrefix('web-service-step');

const { Step } = Steps;
function WebFosterCareService() {
  const dispatch = useDispatch();
  // 獲取國際化配置對象
  const intl = useIntl();
  const { profile } = useSelector(({ user }: { user: UserState }) => user);
  // 從全局狀態中獲取數據
  const { step, maxStep } = useSelector((x: { fosterStepModels: FosterStepModelsState }) => x.fosterStepModels);
  const [isValidated, setIsValidated] = useState(false);
  const [isMarried, setIsMarried] = useState(false);
  // 當前步驟
  const currentStep = stepInfo[step - 1];

  // 更新/初始化models
  const updataModels = usePersistFn((payload) => {
    if (payload.step !== undefined) {
      dispatch({
        type: 'fosterStepModels/update',
        payload,
      });
    }
    if (payload.maxStep !== undefined) {
      dispatch({
        type: 'fosterStepModels/updateMaxStep',
        maxStep: payload.maxStep,
      });
    }
  });

  useEffect(() => {
    // 记录当前到过的最大步骤数，用于步骤间跳转
    if (maxStep < step) {
      updataModels({ maxStep: step });
    }
  }, [step]);

  useEffect(() => {
    if (!profile || !profile.euid) {
      setIsValidated(false);
      return;
    }
    // 婚姻狀態，直接取profile.married
    setIsValidated(profile.married);
    // 檢查是否有資格申請
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const result = validateAgeAndIdentity();
    setIsValidated(result);
  }, [profile]);

  // 澳門居民 25-65歲（本人），按周歲檢查
  const validateAgeAndIdentity = usePersistFn(() => {
    if (!profile || !profile.birthday || !profile.identityType) {
      return true; // 如果没有用户信息，则跳过检查
    }
    const { identityType, birthday } = profile;
    const age = calculateAge(birthday);
    if ((identityType !== '15' && identityType !== '14') || age < 25 || age > 65) {
      Modal.error({
        icon: null,
        content: (
          <div>
            <p style={{ fontWeight: '600', color: '#13a07b' }}>
              {intl.formatMessage({ id: '溫馨提示' })}
            </p>
            <p>{intl.formatMessage({ id: '台端的現況目前不符合申請成為寄養父母的基本條件。若需查詢請致電：83997703 /83997766' })}</p>
          </div>
        ),
        onOk() {
          window.top.close();
        },
      });
      return false;
    }
    return true;
  });

  /**
   * 上一步按鈕點擊
   */
  const handlePrevStep = usePersistFn(async () => {
    updataModels({ step: step - 1 });
  });

  /**
   * 下一步按鈕點擊
   */
  /*  const handleNextStep = useHandleStep(currentStep);*/
  const originalHandleNextStep = useHandleStep(currentStep);
  const handleNextStep = usePersistFn(async () => {
    if (!validateAgeAndIdentity()) {
      return; // 不满足条件时直接返回
    }
    // 判斷是否已婚
    if (!isMarried) { // 注意： 正式環境這裡要修改成 if (!isMarried)
      Modal.error({
        icon: null,
        content: (
          <div>
            <p style={{ fontWeight: '600', color: '#13a07b', marginBottom: '10px' }}>
              {intl.formatMessage({ id: '溫馨提示' })}
            </p>
            <p>{intl.formatMessage({ id: '台端的婚姻狀態不符合申請成為寄養父母的基本條件。若需查詢請致電：8399 7703 /8399 7766' })}</p>
          </div>
        ),
        onOk() {
          // window.top.close();
        },
      });
      return; // 不满足条件时直接返回
    }
    // 否则调用原来的处理逻辑
    // eslint-disable-next-line consistent-return
    return originalHandleNextStep();
  });

  /**
   * 计算步骤条状态
   * @var step 当前步骤 从1开始
   * @var maxStep 最大到达步骤 从1开始
   * @param index 索引 从0开始
   */
  const getStepStatus = usePersistFn((index: number) => {
    if (index + 1 === step) {
      return 'process';
    }
    return undefined;
  });

  return (
    <>
      {isValidated && (
        <>
          <div className={px('root')}>
            {/* 面包屑 */}
            <Breadcrumb itemList={getWebFosterBreadcrumbItems()} />
            {/* 左上角標題 */}
            <h2 className={px('title')}>{intl.formatMessage({ id: '申請成為寄養父母' })}</h2>
            <div className={px('stepinfo')}>
              {/* 左側步驟條 */}
              <div className={px('step')}>
                <h3>{intl.formatMessage({ id: '申请步骤' })}</h3>
                <Steps direction="vertical" size="small" current={step - 1}>
                  {stepInfo.map((info: StepInfoType, index) => (
                    <Step
                      key={info.stepType}
                      title={intl.formatMessage({ id: info.stepName })}
                      icon={index + 1}
                      status={getStepStatus(index)}
                    />
                  ))}
                </Steps>
              </div>
              {/* 內容區標題 */}
              <div className={px('form')}>
                <div className={px('formTitle')}>
                  {currentStep?.stepName ? intl.formatMessage({ id: currentStep.stepName }) : ''}
                </div>
                {/* 主要內容區 */}
                <StepRender
                  currentStep={currentStep}
                  stepInfoList={stepInfo}
                  handleNext={handleNextStep}
                  mobile={false}
                />
              </div>
            </div>
          </div>
          {/* 右側的浮動圖標按鈕 */}
          <GuideButton />
          {/* 底部的按鈕 */}
          <FooterButton
            className={px('footer-button')}
            step={step}
            stepType={currentStep?.stepType}
            handleNextStep={handleNextStep}
            handlePrevStep={handlePrevStep}
          />
        </>
      )}
    </>
  );
}

export default WebFosterCareService;
